/**
 * <scroll-spy-nav>
 *
 * Wraps a sticky subnav ([data-subnav]) and a series of anchored sections.
 * Highlights the subnav link whose section is currently in view and
 * smooth-scrolls to sections on click.
 *
 * Expected markup:
 *   <scroll-spy-nav>
 *     <nav data-subnav>
 *       <a href="#section-id">...</a>
 *     </nav>
 *     <section id="section-id">...</section>
 *   </scroll-spy-nav>
 */
class ScrollSpyNav extends HTMLElement {
  connectedCallback() {
    this.build();
    if (!this.nav || !this.sections.length) return;

    this.currentLink = null;
    this.ticking = false;

    this.onScroll = this.onScroll.bind(this);
    this.onClick = this.onClick.bind(this);
    this.setNavHeight = this.setNavHeight.bind(this);

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.setNavHeight);
    this.nav.addEventListener('click', this.onClick);

    this.setNavHeight();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.setNavHeight);
  }

  // Reads the subnav links and their target sections. Shared by connectedCallback
  // (first run) and refresh() (rebuild after availability filtering changes which
  // sections have visible products) so the two never drift apart.
  build() {
    this.nav = this.querySelector('[data-subnav]');
    if (!this.nav) {
      this.links = [];
      this.sections = [];
      this.visibleSections = [];
      return;
    }

    this.links = Array.from(this.nav.querySelectorAll('a[href^="#"]'));
    this.sections = this.links
      .map((link) => ({
        link,
        item: link.closest('li') || link,
        el: document.getElementById(decodeURIComponent(link.hash.slice(1))),
      }))
      .filter((entry) => entry.el);
    this.visibleSections = this.sections;
  }

  // Public: re-reads which scope headings are currently visible and rebuilds the nav
  // list. A shop-all collection block whose products are all filtered out for the
  // selected delivery week is hidden via .availability-scope-empty
  // (component-availability.css) -- its subnav entry must disappear with it, and it
  // must stop being eligible as the scroll-spy's active section. Called imperatively
  // from assets/availability-filter.js's apply() (not via an event listener) right
  // after that filtering pass runs; see the `scrollSpy.refresh()` call there.
  refresh() {
    if (!this.nav || !this.sections.length) return;

    this.sections.forEach((entry) => {
      const hidden = entry.el.classList.contains('availability-scope-empty');
      entry.item.hidden = hidden;
    });

    this.visibleSections = this.sections.filter(
      (entry) => !entry.el.classList.contains('availability-scope-empty')
    );

    if (this.currentLink && !this.visibleSections.some((entry) => entry.link === this.currentLink)) {
      this.currentLink = null;
      this.links.forEach((link) => link.removeAttribute('aria-current'));
    }

    if (!this.visibleSections.length) return;
    this.update();
  }

  setNavHeight() {
    document.documentElement.style.setProperty('--shop-all-subnav-height', `${this.nav.offsetHeight}px`);
    this.update();
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      this.update();
    });
  }

  update() {
    // Scroll-spying only ever considers currently-visible sections -- a hidden
    // (filtered-empty) section has a collapsed bounding rect and would otherwise
    // falsely win the "top has crossed the line" check below.
    const sections = this.visibleSections.length ? this.visibleSections : this.sections;

    // The activation line sits just below the stuck subnav.
    const line = this.nav.getBoundingClientRect().bottom + 8;

    // Last section whose top has crossed the line wins; default to the first.
    let active = sections[0];
    for (const section of sections) {
      if (section.el.getBoundingClientRect().top <= line) active = section;
    }

    // At the very bottom of the page a short final section may never cross
    // the line — force-activate it.
    if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2) {
      active = sections[sections.length - 1];
    }

    if (active.link === this.currentLink) return;
    this.currentLink = active.link;

    this.links.forEach((link) => link.removeAttribute('aria-current'));
    active.link.setAttribute('aria-current', 'true');

    // Keep the active link visible in the horizontally scrollable nav.
    const list = active.link.closest('ul') || this.nav;
    if (list.scrollWidth > list.clientWidth) {
      list.scrollTo({ left: active.link.offsetLeft - 16, behavior: 'smooth' });
    }
  }

  onClick(event) {
    const link = event.target.closest('a[href^="#"]');
    if (!link) return;

    const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
    if (!target) return;

    event.preventDefault();
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    target.scrollIntoView({ behavior, block: 'start' });
    history.replaceState(null, '', link.hash);
  }
}

if (!customElements.get('scroll-spy-nav')) {
  customElements.define('scroll-spy-nav', ScrollSpyNav);
}
